/**
 * TanStack Query hook for the concept-group prevalence endpoint.
 *
 * The route is gated by `concept_group_prevalence_preview` on the gateway
 * — the hook itself doesn't check the flag. Callers that render
 * conditionally on the flag should pass `enabled` so a flagged-off render
 * never fires the request (and never sees the 404 the gateway returns
 * when the flag is OFF).
 */

// TODO: cleanup with concept_group_prevalence_preview ff

import { useQuery } from '@tanstack/react-query';

import { authFetch } from './authFetch';
import {
  ConceptGroupPrevalenceEnvelopeSchema,
  type ConceptGroupPrevalence,
} from './prevalenceTypes';

/**
 * Stale window. Prevalence is dataset-stable until the underlying
 * concept group is edited (rare) or the dataset is refreshed (also
 * rare — datasets are immutable releases). Five minutes is generous;
 * the dialog is short-lived so most opens hit a fresh query anyway.
 */
const PREVALENCE_STALE_MS = 5 * 60_000;

export interface UseConceptGroupPrevalenceArgs {
  conceptGroupId: string;
  datasetReleaseId: string | null | undefined;
  enabled?: boolean;
}

export const conceptGroupPrevalenceKeys = {
  all: ['concept-group-prevalence'] as const,
  prevalence: (conceptGroupId: string, datasetReleaseId: string) =>
    [...conceptGroupPrevalenceKeys.all, conceptGroupId, datasetReleaseId] as const,
};

export const useConceptGroupPrevalence = ({
  conceptGroupId,
  datasetReleaseId,
  enabled = true,
}: UseConceptGroupPrevalenceArgs) => {
  const hasDataset = typeof datasetReleaseId === 'string' && datasetReleaseId.length > 0;

  return useQuery<ConceptGroupPrevalence>({
    queryKey: conceptGroupPrevalenceKeys.prevalence(conceptGroupId, datasetReleaseId ?? ''),
    queryFn: async () => {
      const params = new URLSearchParams({ datasetReleaseId: datasetReleaseId as string });
      const path = `/concept-groups/${encodeURIComponent(
        conceptGroupId
      )}/prevalence?${params.toString()}`;
      const raw = await authFetch<unknown>(path);
      return ConceptGroupPrevalenceEnvelopeSchema.parse(raw).data;
    },
    enabled: enabled && conceptGroupId.length > 0 && hasDataset,
    staleTime: PREVALENCE_STALE_MS,
  });
};
